import { useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/site/Layout";
import NewsletterForm from "@/components/site/NewsletterForm";
import { newsArticles } from "@/mocks/articles";
import { newsCategories } from "@/mocks/categories";
import { liveFeed } from "@/mocks/live";
import { newsFaq } from "@/mocks/faq";
import { formatViews } from "@/lib/format";
import { usePageMeta } from "@/hooks/usePageMeta";
import type { NewsArticle } from "@/mocks/articles";

export default function Home() {
  usePageMeta({
    title: "吃瓜新闻 - 今日快讯，早报晚报速递与全天直播滚动更新",
    description:
      "吃瓜新闻每日快讯频道，今日要闻、即时速递、早报晚报与直播内容全天滚动更新。快速浏览一天大事，不错过任何重要信息。",
    canonical: "https://youbijing.com.cn/",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "NewsMediaOrganization",
        name: "吃瓜新闻",
        url: "https://youbijing.com.cn/",
        sameAs: [],
        logo: { "@type": "ImageObject", url: "https://youbijing.com.cn/logo.png" },
      },
      {
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: "吃瓜新闻",
        url: "https://youbijing.com.cn/",
        inLanguage: "zh-CN",
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: newsFaq.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ],
  });

  const all = newsArticles;
  const topArticles = all.filter((a) => a.isTop);
  const featured = topArticles[0];
  const ranked = [...all].sort((a, b) => b.views - a.views);
  const hotRank = ranked.slice(0, 5);
  const sideTops = [...topArticles.slice(1), ...ranked.filter((a) => !a.isTop)].slice(0, 4);
  const categoryReps = newsCategories.map((c) => ({
    category: c,
    article: all.filter((a) => a.category === c.slug).sort((x, y) => y.views - x.views)[0],
  }));

  const [filter, setFilter] = useState<string>("all");
  const filteredFeed = (filter === "all" ? all : all.filter((a) => a.category === filter)).slice(0, 6);
  const visibleLive = liveFeed.slice(0, 4);

  function Meta({ a }: { a: NewsArticle }) {
    return (
      <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-foreground-500">
        <span className="text-secondary-700">{a.categoryName}</span>
        <span>{a.dateLabel.slice(5).replace("-", "月")}</span>
        <span className="inline-flex items-center gap-1">
          <i className="ri-eye-line" /> {formatViews(a.views)}
        </span>
      </p>
    );
  }

  return (
    <Layout>
      {/* 报头数据条 */}
      <div className="border-b border-background-200 bg-background-50">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-2 px-4 py-2.5 md:px-6">
          <p className="text-sm text-foreground-600">
            <span className="font-medium text-foreground-800">2026年9月7日</span>
            <span className="mx-2 text-foreground-300">/</span>
            星期一 · 农历七月廿六
          </p>
          <div className="hidden flex-wrap items-center gap-2 sm:flex">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-900">
              <i className="ri-sun-line" /> 今日早报已更新
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-100 px-3 py-1 text-xs font-medium text-accent-900">
              <span className="h-1.5 w-1.5 rounded-full bg-accent-500 animate-pulse-dot" /> 直播滚动中
            </span>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-6xl px-4 py-6 md:px-6 md:py-10">
        {/* 今日要闻 */}
        <section aria-labelledby="today-top" className="pb-10">
          <div className="mb-5 flex items-center gap-3">
            <span className="h-6 w-1.5 rounded-full bg-primary-500" aria-hidden="true" />
            <h2 id="today-top" className="font-heading text-2xl font-black text-foreground-950 md:text-3xl">
              今日要闻
            </h2>
            <span className="text-sm text-foreground-500">Focus of the day</span>
          </div>

          <div className="grid gap-6 lg:grid-cols-3">
            {/* 焦点大图 */}
            <Link
              to={`/article/${featured.slug}`}
              className="group overflow-hidden rounded-xl border border-background-200 bg-background-100 lg:col-span-2"
            >
              <div className="relative overflow-hidden">
                <img
                  src={featured.image}
                  alt={featured.title}
                  title={`${featured.title}吃瓜新闻快讯`}
                  className="aspect-[16/9] w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <span className="absolute left-4 top-4 rounded-md bg-primary-500 px-2.5 py-1 text-xs font-semibold text-background-50">
                  {featured.categoryName}
                </span>
              </div>
              <div className="p-5 md:p-6">
                <h3 className="font-heading text-xl font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-700 md:text-3xl">
                  {featured.title}
                </h3>
                <p className="mt-3 text-sm leading-6 text-foreground-600">{featured.summary}</p>
                <Meta a={featured} />
              </div>
            </Link>

            {/* 侧边要闻列表 */}
            <div className="flex flex-col justify-between rounded-xl border border-background-200 bg-background-50">
              {sideTops.map((a, i) => (
                <Link
                  key={a.id}
                  to={`/article/${a.slug}`}
                  className="group flex flex-1 items-center gap-3 border-b border-background-100 px-4 py-3 transition-colors last:border-b-0 hover:bg-background-100"
                >
                  <span className="font-heading text-2xl font-black text-primary-400">{String(i + 1).padStart(2, "0")}</span>
                  <div className="min-w-0 flex-1">
                    <h4 className="truncate text-sm font-semibold text-foreground-800 group-hover:text-primary-700 md:text-[15px]">
                      {a.title}
                    </h4>
                    <p className="mt-1 text-xs text-foreground-400">{a.timeLabel} · {a.dateLabel}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* 主内容 + 侧栏 */}
        <div className="grid gap-8 lg:grid-cols-3">
          {/* 左栏 最新发布 */}
          <section className="lg:col-span-2" aria-label="最新发布">
            <div className="mb-5 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-3">
                <span className="h-6 w-1.5 rounded-full bg-accent-500" aria-hidden="true" />
                <h2 className="font-heading text-2xl font-black text-foreground-950">最新发布</h2>
              </div>
              <div className="flex flex-wrap gap-1 rounded-full bg-background-100 p-1">
                {["all", ...newsCategories.map((c) => c.slug)].map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setFilter(s)}
                    className={`whitespace-nowrap rounded-full px-3 py-1.5 text-sm transition-colors ${
                      filter === s
                        ? "bg-primary-500 font-medium text-background-50"
                        : "text-foreground-600 hover:text-primary-600"
                    }`}
                  >
                    {s === "all" ? "全部" : newsCategories.find((c) => c.slug === s)?.name}
                  </button>
                ))}
              </div>
            </div>

            <div className="overflow-hidden rounded-xl border border-background-200 bg-background-50">
              {filteredFeed.map((a, idx) => (
                <Link
                  key={a.id}
                  to={`/article/${a.slug}`}
                  className={`group flex flex-col gap-4 p-4 transition-colors hover:bg-background-100 sm:flex-row sm:items-center sm:px-5 ${
                    idx !== filteredFeed.length - 1 ? "border-b border-background-100" : ""
                  }`}
                >
                  <img
                    src={a.image}
                    alt={a.title}
                    title={`${a.title}吃瓜新闻速递`}
                    className="h-44 w-full shrink-0 rounded-lg object-cover sm:h-24 sm:w-36"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="mb-1.5 flex flex-wrap items-center gap-2">
                      <span className="rounded-md bg-secondary-100 px-2 py-0.5 text-xs font-medium text-secondary-900">
                        {a.categoryName}
                      </span>
                      {a.tags.slice(0, 1).map((t) => (
                        <span key={t} className="text-xs text-foreground-400">#{t}</span>
                      ))}
                    </div>
                    <h3 className="font-heading text-lg font-bold leading-snug text-foreground-900 group-hover:text-primary-700">
                      {a.title}
                    </h3>
                    <p className="mt-1 hidden text-sm leading-6 text-foreground-500 sm:line-clamp-1">{a.summary}</p>
                    <Meta a={a} />
                  </div>
                  <span className="hidden shrink-0 rounded-full border border-background-200 px-3 py-1.5 text-xs text-foreground-500 transition-colors group-hover:border-primary-300 group-hover:text-primary-600 sm:block">
                    阅读全文
                  </span>
                </Link>
              ))}
            </div>
          </section>

          {/* 右栏 */}
          <aside className="space-y-8" aria-label="热点侧栏">
            {/* 热门榜 */}
            <section className="overflow-hidden rounded-xl border border-background-200 bg-background-50">
              <div className="flex items-center justify-between border-b border-background-100 px-5 py-4">
                <h3 className="flex items-center gap-2 font-heading text-lg font-black text-foreground-950">
                  <i className="ri-fire-fill text-primary-500" /> 热度TOP榜
                </h3>
                <span className="text-xs text-foreground-400">按阅读量</span>
              </div>
              <ol className="divide-y divide-background-100">
                {hotRank.map((a, i) => (
                  <li key={a.id}>
                    <Link
                      to={`/article/${a.slug}`}
                      className="group flex items-start gap-3 px-5 py-3.5 transition-colors hover:bg-background-100"
                    >
                      <span
                        className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-xs font-bold ${
                          i < 3 ? "bg-primary-500 text-background-50" : "bg-background-200 text-foreground-600"
                        }`}
                      >
                        {i + 1}
                      </span>
                      <span className="min-w-0">
                        <span className="line-clamp-2 text-sm font-medium leading-5 text-foreground-800 group-hover:text-primary-700">
                          {a.title}
                        </span>
                        <span className="mt-1 flex items-center gap-2 text-xs text-foreground-400">
                          <i className="ri-eye-line" /> {formatViews(a.views)} 阅读
                        </span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ol>
            </section>

            {/* 直播速览 */}
            <section className="rounded-xl border border-background-200 bg-background-100 p-5">
              <div className="mb-4 flex items-center justify-between">
                <h3 className="flex items-center gap-2 font-heading text-lg font-black text-foreground-950">
                  <span className="h-2 w-2 rounded-full bg-primary-500 animate-pulse-dot" />
                  直播速览
                </h3>
                <Link to="/live" className="text-xs font-medium text-primary-600 hover:text-primary-700">
                  查看全部
                  <i className="ri-arrow-right-line ml-1" />
                </Link>
              </div>
              <ul className="relative space-y-4 pl-4">
                <span className="absolute left-1.5 top-1 h-[calc(100%-8px)] w-px bg-background-200" aria-hidden="true" />
                {visibleLive.map((item) => (
                  <li key={item.id} className="relative">
                    <span className="absolute -left-[17px] top-1.5 h-2 w-2 rounded-full border-2 border-accent-500 bg-background-50" />
                    <span className="text-xs font-semibold text-accent-700">{item.time}</span>
                    <p className="mt-0.5 text-sm leading-5 text-foreground-700">{item.text}</p>
                  </li>
                ))}
              </ul>
            </section>

            {/* 栏目入口 */}
            <section className="rounded-xl border border-background-200 bg-background-50 p-5">
              <h3 className="font-heading text-lg font-black text-foreground-950">快速看栏目</h3>
              <div className="mt-4 grid grid-cols-2 gap-2">
                {newsCategories.map((c) => (
                  <Link
                    key={c.slug}
                    to={`/category/${c.slug}`}
                    className="group flex items-center gap-2 rounded-lg border border-background-200 bg-background-50 px-3 py-2.5 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
                  >
                    <i className={`${c.icon} text-primary-500`} />
                    {c.name}
                  </Link>
                ))}
                <Link
                  to="/about"
                  className="group flex items-center gap-2 rounded-lg bg-primary-500 px-3 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-information-line" />
                  关于我们
                </Link>
              </div>
            </section>
          </aside>
        </div>

        {/* 栏目速览 */}
        <section className="pt-12" aria-label="栏目速览">
          <div className="mb-5 flex items-center gap-3">
            <span className="h-6 w-1.5 rounded-full bg-secondary-500" aria-hidden="true" />
            <h2 className="font-heading text-2xl font-black text-foreground-950 md:text-3xl">栏目速览</h2>
          </div>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {categoryReps.map(({ category, article }) => (
              <div key={category.slug} className="flex flex-col overflow-hidden rounded-xl border border-background-200 bg-background-50">
                <div className="flex items-center justify-between border-b border-background-100 px-5 py-4">
                  <span className="flex items-center gap-2 font-heading text-base font-bold text-foreground-900">
                    <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-accent-100 text-accent-700">
                      <i className={category.icon} />
                    </span>
                    {category.name}
                  </span>
                  <Link
                    to={`/category/${category.slug}`}
                    className="flex items-center gap-0.5 text-xs font-medium text-primary-600 hover:text-primary-700"
                  >
                    进栏目 <i className="ri-arrow-right-line" />
                  </Link>
                </div>
                <Link to={`/article/${article.slug}`} className="group flex flex-1 flex-col p-5">
                  <img
                    src={article.image}
                    alt={article.title}
                    title={`${article.title}吃瓜新闻栏目推荐`}
                    className="aspect-[16/10] w-full rounded-lg object-cover"
                  />
                  <h3 className="mt-3 font-heading text-base font-bold leading-snug text-foreground-900 group-hover:text-primary-700">
                    {article.title}
                  </h3>
                  <p className="mt-1.5 line-clamp-2 text-sm leading-5 text-foreground-500">{article.summary}</p>
                </Link>
              </div>
            ))}
          </div>
        </section>

        {/* 常见问题 */}
        <section className="py-12" aria-label="常见问题">
          <div className="mb-6 text-center">
            <span className="text-sm font-medium text-primary-600">FAQ · 常见问题</span>
            <h2 className="mt-1 font-heading text-2xl font-black text-foreground-950 md:text-3xl">你想了解的小事</h2>
          </div>
          <div className="mx-auto max-w-3xl divide-y divide-background-200 overflow-hidden rounded-xl border border-background-200 bg-background-50">
            {newsFaq.map((f, i) => (
              <details key={f.q} open={i === 0} className="group px-5 py-4">
                <summary className="flex cursor-pointer list-none items-center justify-between gap-3 text-[15px] font-semibold text-foreground-900">
                  <span>{f.q}</span>
                  <i className="ri-add-line text-lg text-foreground-400 transition-transform group-open:rotate-45" />
                </summary>
                <p className="mt-3 pl-1 text-sm leading-6 text-foreground-600">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        {/* 订阅 */}
        <section className="pb-4 pt-2">
          <NewsletterForm />
        </section>
      </div>
    </Layout>
  );
}